import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Form, 
  Input, 
  Button, 
  Select, 
  DatePicker, 
  TimePicker, 
  Upload, 
  message, 
  Tabs, 
  Card, 
  Space, 
  Tag, 
  Popconfirm,
  Switch,
  Row,
  Col,
  Divider,
  Tooltip,
  Alert,
  Spin
} from 'antd';
import { 
  PlusOutlined, 
  DeleteOutlined, 
  UploadOutlined,
  EditOutlined,
  CloseOutlined,
  PushpinOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import moment, { Moment } from 'moment';
import { 
  getCategories, 
  createCategory, 
  deleteCategory,
  createNews,
  updateNews,
  deleteNews,
  uploadNewsImage,
  deleteNewsImage,
  getNews,
  NewsItem,
  NewsCategory
} from '../services/api';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import { canCreateCategories } from '../utils/roleUtils';
import { getUserRole } from '../utils/cognitoUtils';
import './EditNewsModal.css';

const { TextArea } = Input;
const { Option } = Select;

interface EditNewsModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingNews?: NewsItem | null;
}

const EditNewsModal: React.FC<EditNewsModalProps> = ({
  open,
  onClose,
  onSuccess,
  editingNews
}) => {
  const { user } = useCognitoAuth();
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('create');
  const [categories, setCategories] = useState<NewsCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingNews, setLoadingNews] = useState(false);
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [newsPage, setNewsPage] = useState(1);
  const [newsTotalPages, setNewsTotalPages] = useState(1);
  
  // Form states
  const [newsType, setNewsType] = useState<'standard' | 'poll'>('standard');
  const [pollOptions, setPollOptions] = useState<string[]>(['']);
  const [pollDuration, setPollDuration] = useState<string>('1day');
  const [pollCustomDate, setPollCustomDate] = useState<Moment | null>(null);
  const [pollCustomTime, setPollCustomTime] = useState<Moment | null>(null);
  const [showResultsLive, setShowResultsLive] = useState(true);
  const [allowMultipleVotes, setAllowMultipleVotes] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<Array<{ id?: number; url: string; file?: File }>>([]);
  const [newCategoryTag, setNewCategoryTag] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState('#1890ff');

  const canCreateCat = user ? canCreateCategories(getUserRole(user)) : false;

  useEffect(() => {
    if (open) {
      fetchCategories();
      if (activeTab === 'manage') {
        fetchAllNews();
      }
      if (editingNews) {
        loadNewsForEdit();
      } else {
        form.resetFields();
        setNewsType('standard');
        setPollOptions(['']);
        setUploadedImages([]);
      }
    }
  }, [open, activeTab, editingNews]);

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await getCategories();
      if (response.success) {
        setCategories(response.data);
      }
    } catch (error) {
      message.error('Erro ao carregar categorias');
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchAllNews = async (page: number = 1) => {
    setLoadingNews(true);
    try {
      const response = await getNews({ page, per_page: 10 });
      if (response.success) {
        setNewsItems(response.data);
        setNewsTotalPages(response.pagination.pages);
        setNewsPage(page);
      }
    } catch (error) {
      message.error('Erro ao carregar notícias');
    } finally {
      setLoadingNews(false);
    }
  };

  const loadNewsForEdit = () => {
    if (!editingNews) return;
    
    form.setFieldsValue({
      title: editingNews.title,
      description: editingNews.description,
      category_tag: editingNews.category_tag,
      is_pinned: editingNews.is_pinned,
      news_type: editingNews.news_type === 'announcement' ? 'standard' : editingNews.news_type
    });
    
    setNewsType(editingNews.news_type === 'announcement' ? 'standard' : editingNews.news_type);
    
    if (editingNews.poll) {
      setPollOptions(editingNews.poll.options.map(opt => opt.text));
      setShowResultsLive(editingNews.poll.show_results_live);
      setAllowMultipleVotes(editingNews.poll.allow_multiple_votes);
      const closesAt = moment(editingNews.poll.closes_at);
      setPollCustomDate(closesAt);
      setPollCustomTime(closesAt);
    }
    
    if (editingNews.images) {
      setUploadedImages(editingNews.images.map(img => ({ id: img.id, url: img.image_url })));
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryTag || !newCategoryName) {
      message.warning('Preencha o tag e o nome da categoria');
      return;
    }
    
    try {
      const response = await createCategory({
        tag: newCategoryTag.toLowerCase().replace(/\s+/g, '_'),
        display_name: newCategoryName,
        color: newCategoryColor
      });
      
      if (response.success) {
        message.success('Categoria criada com sucesso');
        setNewCategoryTag('');
        setNewCategoryName('');
        setNewCategoryColor('#1890ff');
        fetchCategories();
        // Notify parent component to refresh categories
        onSuccess();
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Erro ao criar categoria');
    }
  };

  const handleDeleteCategory = async (categoryId: number) => {
    try {
      const response = await deleteCategory(categoryId);
      if (response.success) {
        message.success(response.message);
        fetchCategories();
        // Notify parent component to refresh categories
        onSuccess();
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Erro ao eliminar categoria');
    }
  };

  const handleImageUpload = async (file: File, newsId?: number) => {
    try {
      if (newsId) {
        // Upload to existing news
        const response = await uploadNewsImage(newsId, file, uploadedImages.length);
        if (response.success) {
          setUploadedImages(prev => [...prev, { id: response.data.id, url: response.data.image_url }]);
          message.success('Imagem carregada com sucesso');
        }
      } else {
        // Store for new news (will upload after news creation)
        const reader = new FileReader();
        reader.onload = (e) => {
          setUploadedImages(prev => [...prev, { url: e.target?.result as string, file }]);
        };
        reader.readAsDataURL(file);
      }
    } catch (error) {
      message.error('Erro ao carregar imagem');
    }
    return false; // Prevent auto upload
  };

  const handleRemoveImage = async (index: number, imageId?: number) => {
    if (imageId) {
      try {
        await deleteNewsImage(imageId);
        message.success('Imagem removida');
      } catch (error) {
        message.error('Erro ao remover imagem');
      }
    }
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  const calculatePollClosesAt = (): string => {
    if (pollDuration === 'custom' && pollCustomDate && pollCustomTime) {
      const combined = moment(pollCustomDate).set({
        hour: pollCustomTime.hour(),
        minute: pollCustomTime.minute(),
        second: 0
      });
      return combined.toISOString();
    }
    
    const durations: Record<string, number> = {
      '1hour': 1,
      '6hours': 6,
      '1day': 24,
      '3days': 72,
      '1week': 168
    };
    
    const hours = durations[pollDuration] || 24;
    return moment().add(hours, 'hours').toISOString();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      const newsData: any = {
        title: values.title,
        description: values.description || '',
        category_tag: values.category_tag || null,
        is_pinned: values.is_pinned || false,
        news_type: newsType,
        create_notification: values.create_notification || false
      };
      
      if (newsType === 'poll') {
        const validOptions = pollOptions.filter(opt => opt.trim());
        if (validOptions.length < 2) {
          message.warning('Uma votação precisa de pelo menos 2 opções');
          setLoading(false);
          return;
        }
        
        newsData.poll = {
          question: values.poll_question || values.title,
          options: validOptions,
          closes_at: calculatePollClosesAt(),
          allow_multiple_votes: allowMultipleVotes,
          show_results_live: showResultsLive
        };
      }
      
      let newsId: number;
      
      if (editingNews) {
        // Update existing news
        const response = await updateNews(editingNews.id, newsData);
        if (response.success) {
          newsId = editingNews.id;
          message.success('Notícia atualizada com sucesso');
        } else {
          throw new Error('Erro ao atualizar');
        }
      } else {
        // Create new news
        const response = await createNews(newsData);
        if (response.success) {
          newsId = response.data.id;
          message.success('Notícia criada com sucesso');
        } else {
          throw new Error('Erro ao criar');
        }
      }
      
      // Upload images for new news
      if (!editingNews && uploadedImages.length > 0) {
        for (const img of uploadedImages) {
          if (img.file) {
            await uploadNewsImage(newsId, img.file, uploadedImages.indexOf(img));
          }
        }
      }
      
      form.resetFields();
      setNewsType('standard');
      setPollOptions(['']);
      setUploadedImages([]);
      setLoading(false);
      onSuccess();
      onClose();
    } catch (error: any) {
      setLoading(false);
      message.error(error.response?.data?.message || 'Erro ao salvar notícia');
    }
  };

  const handleDeleteNews = async (newsId: number) => {
    try {
      await deleteNews(newsId);
      message.success('Notícia eliminada com sucesso');
      fetchAllNews(newsPage);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Erro ao eliminar notícia');
    }
  };

  return (
    <Modal
      title={editingNews ? 'Editar Notícia' : 'Criar Notícia'}
      open={open}
      onCancel={onClose}
      width={900}
      footer={null}
      destroyOnClose
      className="edit-news-modal"
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
        {
          key: 'create',
          label: 'Criar/Editar Notícia',
          children: (
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            {/* Info box about categories */}
            <Alert
              message={<span style={{ color: '#ffffff' }}>Categoria</span>}
              description="A categoria determina em qual tab a notícia aparece. Todas as notícias aparecem também na tab 'Todas'."
              type="info"
              icon={<InfoCircleOutlined />}
              style={{ 
                marginBottom: 16,
                background: 'rgba(30, 58, 138, 0.4)',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                color: '#ffffff'
              }}
              showIcon
            />

            <Form.Item
              name="category_tag"
              label={<span style={{ color: '#ffffff' }}>Categoria</span>}
              rules={[{ required: true, message: 'Selecione uma categoria' }]}
            >
              <Select placeholder="Selecione uma categoria" allowClear>
                {categories.map(cat => (
                  <Option key={cat.id} value={cat.tag}>
                    @{cat.display_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="news_type"
              label="Tipo de Notícia"
              initialValue="standard"
              rules={[{ required: true, message: 'Selecione o tipo' }]}
            >
              <Select value={newsType} onChange={setNewsType}>
                <Option value="standard">Notícia Padrão</Option>
                <Option value="poll">Votação</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="title"
              label="Título"
              rules={[{ required: true, message: 'O título é obrigatório' }]}
            >
              <Input placeholder="Digite o título da notícia" />
            </Form.Item>

            <Form.Item
              name="description"
              label="Descrição"
            >
              <TextArea 
                rows={6} 
                placeholder="Digite a descrição da notícia"
              />
            </Form.Item>

            {newsType === 'poll' && (
              <>
                <Form.Item
                  name="poll_question"
                  label="Pergunta da Votação"
                >
                  <Input placeholder="Digite a pergunta (opcional, usa o título se vazio)" />
                </Form.Item>

                <div style={{ marginBottom: 16 }}>
                  <label>Opções de Votação</label>
                  {pollOptions.map((opt, index) => (
                    <div key={index} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <Input
                        value={opt}
                        onChange={(e) => {
                          const newOptions = [...pollOptions];
                          newOptions[index] = e.target.value;
                          setPollOptions(newOptions);
                        }}
                        placeholder={`Opção ${index + 1}`}
                      />
                      {pollOptions.length > 1 && (
                        <Button
                          icon={<DeleteOutlined />}
                          onClick={() => setPollOptions(pollOptions.filter((_, i) => i !== index))}
                        />
                      )}
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => setPollOptions([...pollOptions, ''])}
                    block
                    style={{ marginTop: 8 }}
                  >
                    Adicionar Opção
                  </Button>
                </div>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Duração da Votação">
                      <Select value={pollDuration} onChange={setPollDuration}>
                        <Option value="1hour">1 Hora</Option>
                        <Option value="6hours">6 Horas</Option>
                        <Option value="1day">1 Dia</Option>
                        <Option value="3days">3 Dias</Option>
                        <Option value="1week">1 Semana</Option>
                        <Option value="custom">Personalizado</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  {pollDuration === 'custom' && (
                    <>
                      <Col span={6}>
                        <Form.Item label="Data">
                          <DatePicker
                            value={pollCustomDate}
                            onChange={setPollCustomDate}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item label="Hora">
                          <TimePicker
                            value={pollCustomTime}
                            onChange={setPollCustomTime}
                            format="HH:mm"
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                    </>
                  )}
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Mostrar Resultados">
                      <Switch
                        checked={showResultsLive}
                        onChange={setShowResultsLive}
                        checkedChildren="Em tempo real"
                        unCheckedChildren="Apenas no fim"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Permitir Múltiplas Votações">
                      <Switch
                        checked={allowMultipleVotes}
                        onChange={setAllowMultipleVotes}
                        checkedChildren="Sim"
                        unCheckedChildren="Não"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}

            <Divider style={{ margin: '16px 0', borderColor: 'rgba(255, 255, 255, 0.2)' }} />

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="is_pinned"
                  label={
                    <Space>
                      <span>Fixar Notícia</span>
                      <Tooltip title="Notícias fixadas aparecem sempre no topo da lista, antes das outras notícias">
                        <InfoCircleOutlined style={{ color: 'rgba(255, 255, 255, 0.6)' }} />
                      </Tooltip>
                    </Space>
                  }
                  valuePropName="checked"
                >
                  <Switch checkedChildren={<PushpinOutlined />} unCheckedChildren="Normal" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="create_notification"
                  label="Criar Notificação para Todos"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="Sim" unCheckedChildren="Não" />
                </Form.Item>
              </Col>
            </Row>

            <Divider style={{ margin: '16px 0', borderColor: 'rgba(255, 255, 255, 0.2)' }}>
              Imagens
            </Divider>
            
            <Upload
              beforeUpload={(file) => {
                handleImageUpload(file, editingNews?.id);
                return false;
              }}
              showUploadList={false}
              accept="image/*"
            >
              <Button icon={<UploadOutlined />}>Carregar Imagem</Button>
            </Upload>

            {uploadedImages.length > 0 && (
              <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {uploadedImages.map((img, index) => (
                  <div key={index} style={{ position: 'relative', display: 'inline-block' }}>
                    <img
                      src={img.url}
                      alt={`Upload ${index + 1}`}
                      style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8 }}
                    />
                    <Button
                      type="text"
                      danger
                      icon={<CloseOutlined />}
                      size="small"
                      style={{ position: 'absolute', top: 0, right: 0 }}
                      onClick={() => handleRemoveImage(index, img.id)}
                    />
                  </div>
                ))}
              </div>
            )}

            <Form.Item style={{ marginTop: 24 }}>
              <Space>
                <Button type="primary" htmlType="submit" loading={loading}>
                  {editingNews ? 'Atualizar' : 'Criar'} Notícia
                </Button>
                <Button onClick={onClose}>Cancelar</Button>
              </Space>
            </Form.Item>
          </Form>
          )
        },
        {
          key: 'categories',
          label: 'Gerir Categorias',
          children: (
            <>
              {loadingCategories ? (
                <div style={{ position: 'relative', minHeight: '300px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Spin size="large" />
                </div>
              ) : canCreateCat ? (
                <>
                  <Card title="Criar Nova Categoria" style={{ marginBottom: 16 }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Input
                        placeholder="Tag (ex: notificações)"
                        value={newCategoryTag}
                        onChange={(e) => setNewCategoryTag(e.target.value)}
                      />
                      <Input
                        placeholder="Nome de Exibição (ex: Notificações)"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                      />
                      <Input
                        type="color"
                        value={newCategoryColor}
                        onChange={(e) => setNewCategoryColor(e.target.value)}
                        style={{ width: 100 }}
                      />
                      <Button type="primary" onClick={handleCreateCategory}>
                        Criar Categoria
                      </Button>
                    </Space>
                  </Card>

                  <div>
                    <h4 style={{ color: '#ffffff' }}>Categorias Existentes</h4>
                    {categories.map(cat => (
                      <Card key={cat.id} style={{ marginBottom: 8 }}>
                        <Space>
                          <Tag color={cat.color || '#1890ff'}>@{cat.display_name}</Tag>
                          <Popconfirm
                            title="Tem certeza? Todas as notícias desta categoria serão eliminadas."
                            onConfirm={() => handleDeleteCategory(cat.id)}
                            okText="Sim"
                            cancelText="Não"
                          >
                            <Button danger size="small" icon={<DeleteOutlined />}>
                              Eliminar
                            </Button>
                          </Popconfirm>
                        </Space>
                      </Card>
                    ))}
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <p>Sem permissão para gerir categorias</p>
                </div>
              )}
            </>
          )
        },
        {
          key: 'manage',
          label: 'Ver Todas as Notícias',
          children: (
            <div>
              {loadingNews ? (
                <div style={{ position: 'relative', minHeight: '300px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Spin size="large" />
                </div>
              ) : (
                newsItems.map(item => (
                  <Card key={item.id} style={{ marginBottom: 8 }}>
                    <Space>
                      <strong>{item.title}</strong>
                      <Tag>{item.category_tag ? `@${item.category_tag}` : 'Sem categoria'}</Tag>
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => {
                          // This would need to be handled by parent component
                          message.info('Funcionalidade de edição em desenvolvimento');
                        }}
                      >
                        Editar
                      </Button>
                      <Popconfirm
                        title="Tem certeza que deseja eliminar esta notícia?"
                        onConfirm={() => handleDeleteNews(item.id)}
                        okText="Sim"
                        cancelText="Não"
                      >
                        <Button danger size="small" icon={<DeleteOutlined />}>
                          Eliminar
                        </Button>
                      </Popconfirm>
                    </Space>
                  </Card>
                ))
              )}
            </div>
          )
        }
      ]} />
    </Modal>
  );
};

export default EditNewsModal;

